import type { Chunk, FindChunks } from 'react-highlight-words'
import { isEmpty } from 'lodash'
import type {
  CreateDownloadJobErrorResponse,
  DownloadJobStatusResponse,
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
  RecentSearches,
} from '../../interfaces'
import {
  downloadFileFromUrl,
  isEditableImage,
} from '../../../utils/mediaLibrary'
import {
  removeLeadingSlash,
  removeTrailingSlash,
} from '../../../utils/urlHelpers'

export const mediaCollectionNameRegex = /^(?!.*\/).*$/u

// Helper functions for recent searches

const MAX_SEARCHES = 15
const STORAGE_KEY = 'recentSearches'
const COLLECTION_STORAGE_KEY = 'recentSearchesForCollections'

// Helper function to get recent searches from local storage
const getRecentSearches = (storageKey: string): RecentSearches => {
  const storedSearches = localStorage.getItem(storageKey)
  return storedSearches ? (JSON.parse(storedSearches) as RecentSearches) : {}
}

// Helper function to save recent searches to local storage
const saveRecentSearches = (
  recentSearches: RecentSearches,
  storageKey: string,
): void => {
  localStorage.setItem(storageKey, JSON.stringify(recentSearches))
}

// Function to get recent searches in descending order of recency
export const getRecentSearchesDescending = (
  forMediaCollection = false,
): RecentSearches => {
  const storageKey = forMediaCollection ? COLLECTION_STORAGE_KEY : STORAGE_KEY
  const recentSearches: RecentSearches = getRecentSearches(storageKey)

  const b = Object.entries(recentSearches)
    .sort(([, timestampA], [, timestampB]) => timestampB - timestampA)
    .reduce((acc, [s, t]) => ({ ...acc, [s]: t }), {})
  return b
}

// Function to insert a recent search
export const insertRecentSearch = (
  search: string,
  forMediaCollection = false,
): void => {
  if (!search) {
    return
  }
  const storageKey = forMediaCollection ? COLLECTION_STORAGE_KEY : STORAGE_KEY
  const recentSearches: RecentSearches = getRecentSearches(storageKey)

  // Add or update the timestamp for the search
  recentSearches[search] = Date.now()

  // Keep only the latest MAX_SEARCHES searches
  const sortedSearches = Object.entries(recentSearches)
    .sort(([, timestampA], [, timestampB]) => timestampB - timestampA)
    .slice(0, MAX_SEARCHES)
    .reduce((acc, [s, t]) => ({ ...acc, [s]: t }), {})

  saveRecentSearches(sortedSearches, storageKey)
}

// Helper function for highlighting search terms
export const findChunksAtBeginningOfWords = ({
  autoEscape,
  caseSensitive,
  sanitize,
  searchWords,
  textToHighlight,
}: FindChunks): Chunk[] => {
  const chunks: Chunk[] = []
  const textLow = textToHighlight.toLowerCase()

  // Split on every non-word character
  // eslint-disable-next-line require-unicode-regexp
  const sep = /[\W_.]+/

  const splittedSearchWordsArr = searchWords.map(sw =>
    (sw as string).split(sep),
  )

  searchWords = splittedSearchWordsArr.flat()

  searchWords = searchWords.filter(s => !isEmpty(s))

  const singleTextWords = textLow.split(sep)

  let fromIndex = 0
  const singleTextWordsWithPos = singleTextWords.map(s => {
    const indexInWord = textLow.indexOf(s, fromIndex)
    fromIndex = indexInWord + s.length
    return {
      word: s,
      index: indexInWord,
    }
  })

  // Add chunks for every searchWord
  searchWords.forEach(sw => {
    const swLow = (sw as string).toLowerCase()
    // Do it for every single text word
    singleTextWordsWithPos.forEach(s => {
      if (s.word.startsWith(swLow)) {
        const start = s.index
        const end = s.index + swLow.length
        chunks.push({
          start,
          end,
        })
      }
    })
  })

  return chunks
}

export function normalizeMime(mime: string | null): string {
  return (mime ?? '').split(';')[0].trim().toLowerCase()
}

export function stripExtension(name: string): string {
  return name.replace(/\.[^./\\]*$/, '')
}

export function filenameFromUrl(url: string): string {
  try {
    const u = new URL(url)
    const last = u.pathname.split('/').pop() || ''
    return last ? decodeURIComponent(last) : 'download'
  } catch {
    return 'download'
  }
}

export function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
}

export function dedupeFilename(filename: string, used: Map<string, number>): string {
  if (!used.has(filename)) {
    used.set(filename, 1)
    return filename
  }
  const dot = filename.lastIndexOf('.')
  const base = dot > -1 ? filename.slice(0, dot) : filename
  const ext = dot > -1 ? filename.slice(dot) : ''
  let i = used.get(filename)! + 1
  let candidate = `${base} (${i})${ext}`
  while (used.has(candidate)) {
    i++
    candidate = `${base} (${i})${ext}`
  }
  used.set(filename, i)
  used.set(candidate, 1)
  return candidate
}


// MongoId Check is hex string of 24 characters code copied from mongo
export const CheckValidObjectId = (string: string): boolean => {
  if (string?.length !== 24) return false;
  for (let i = 0; i < 24; i++) {
    const char = string.charCodeAt(i);
    if (
      // Check for ASCII 0-9
      (char >= 48 && char <= 57) ||
      // Check for ASCII a-f
      (char >= 97 && char <= 102) ||
      // Check for ASCII A-F
      (char >= 65 && char <= 70)
    ) {
      continue
    }
    return false
  }
  return true
}
